import React, { useState, useEffect } from "react";
import { Button, Card, Progress, Statistic } from "antd";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { BaseUrl } from "../../../../Constants/Constants";
import FeedbackModal from "../../../../Feedback/FeedbackModal";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HomeIcon from "@mui/icons-material/Home";
import RateReviewIcon from "@mui/icons-material/RateReview";
import "./ManualResult.css";

const ManualResult = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, assessmentId} = useParams();
  const localEligibility = localStorage.getItem("course_eligibility");
  const profileDetails = JSON.parse(localStorage.getItem("Profile"));
  const userId = profileDetails.user_id;

  // ✅ GET SCORES FROM NAVIGATION STATE
  const [userScore, setUserScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [Data, setData] = useState("");
  const [timer, setTimer] = useState(10);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackAvailable, setFeedbackAvailable] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    if (
      location.state?.userScore !== undefined &&
      location.state?.totalScore !== undefined
    ) {
      setUserScore(location.state.userScore);
      setTotalScore(location.state.totalScore);
    } else {
      console.log("No scores in navigation state, fetching from API...");
    }
    setLoading(false); 
  }, [location.state]);

  useEffect(() => {
    // Push a dummy state so back button can be intercepted
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      // Prevent default back behavior
      window.history.pushState(null, "", window.location.href);
      // Redirect to dashboard
      navigate("/courses", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  const getScoreColor = () => {
    if (totalScore === 0) return "#667eea";

    const percentage = (userScore / totalScore) * 100;

    if (percentage >= 80) {
      return "#4caf50"; // Green
    } else if (percentage >= 50) {
      return "#ffa726"; // Orange
    } else {
      return "#ff4c4c"; // Red
    }
  };

  // Prefetch feedback forms -> feedback form only come when the form created and asign to course from admin side
  useEffect(() => {
    if (!id) return;
    axios
      .post(
        `${BaseUrl}feedback-form/list_of_feedback_forms`,
        { course_id: id },
        { withCredentials: true },
      )
      .then((res) => {
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setFeedbackAvailable(list.length > 0);
      })
      .catch(() => setFeedbackAvailable(false));
  }, [id]);

  // Countdown timer
  useEffect(() => {
    if (feedbackAvailable === null) return; 
    if (feedbackAvailable === false) return; 
    
    if (timer === 0) {
      setShowFeedbackModal(true);
      return;
    }
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [timer, feedbackAvailable]);

  const handleDashboard = () => {
    if (feedbackAvailable) {
      setShowFeedbackModal(true);
    } else {
      navigate(`/`);
    }
  };

  const handleFeedbackSuccess = () => {
    setShowFeedbackModal(false);
    navigate(`/`);
  };

  // ✅ CALCULATE PERCENTAGE BASED ON SCORES
  const scorePercentage = React.useMemo(() => {
    if (!userScore && !totalScore) return 0;
    if (totalScore === 0) return 0;

    const percent = (userScore / totalScore) * 100;
    return Math.min(100, Math.max(0, Math.round(percent))); 
  }, [userScore, totalScore]);

  // ✅ HANDLE REVIEW BUTTON CLICK
  const handleReviewAnswers = () => {
    navigate(`/courses/${id}/assessment/${assessmentId}/ReviewAnswerModule`);
  };

  if (loading) {
    return (
      <div className="mcqResultContainer">
        <div className="mcqResultContent">
          <Card className="mcqResultCard">
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Progress type="circle" percent={50} status="active" />
              <p style={{ marginTop: "20px" }}>Loading your results...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="mcqResultContainer">
      <div className="mcqResultContent">
        <Card className="mcqResultCard">
          {/* Celebration Section */}
          <div className="mcqResultCelebration">
            <div className="mcqResultIconWrapper">
              <EmojiEventsIcon className="mcqResultIcon" />
            </div>
            <h1 className="mcqResultTitle">Congratulations!</h1>
            <p className="mcqResultSubtitle">
              You've successfully completed the assessment
            </p>
          </div>

          {/* Feedback Timer */}
          {!showFeedbackModal && feedbackAvailable && (
            <div className="mcqResultFeedbackTimer">
              <AccessTimeIcon className="mcqResultFeedbackTimerIcon" />
              <div className="mcqResultFeedbackTimerContent">
                <span className="mcqResultFeedbackTimerLabel">
                  Feedback form available in
                </span>
                <span className="mcqResultFeedbackTimerValue">
                  {timer} second{timer !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}

          {/* ✅ SCORE DISPLAY USING SCORES */}
          {Data && Data.assessment_type === "mcq with description" ? (
            <div className="mcqResultScoreCard descriptionType">
              <CheckCircleIcon className="mcqResultCheckIcon" />
              <p className="mcqResultDescriptionText">
                Scores will be sent to you via email
              </p>
            </div>
          ) : (
            <div className="mcqResultScoreSection">
              <div
                className="mcqResultScoreCard"
                style={{ borderColor: getScoreColor() }}
              >
                <div className="mcqResultScoreDisplay">
                  <span
                    className="mcqResultScoreValue"
                    style={{ color: getScoreColor() }}
                  >
                    {userScore}
                  </span>
                  <span className="mcqResultScoreDivider">/</span>
                  <span className="mcqResultScoreTotal">{totalScore}</span>
                </div>
                <div className="mcqResultScoreLabel">Your Test Score</div>
              </div>

              {/* Progress Circle */}
              <div className="mcqResultProgressSection">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "180px",
                  }}
                >
                  <Progress
                    type="circle"
                    percent={scorePercentage}
                    strokeColor={getScoreColor()}
                    strokeWidth={8}
                    format={() => `${scorePercentage}%`}
                  />
                </div>

                <div className="mcqResultProgressInfo">
                  <Statistic
                    title="Performance"
                    value={scorePercentage}
                    suffix="%"
                    valueStyle={{
                      color: getScoreColor(),
                      fontSize: "32px",
                      fontWeight: 700,
                    }}
                  />
                  <p className="mcqResultPerformanceText">
                    {scorePercentage >= 80
                      ? "Excellent! You've mastered this assessment."
                      : scorePercentage >= 50
                      ? "Good job! Keep practicing to improve."
                      : "Don't worry! Review the material and try again."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ✅ ACTION BUTTONS - DASHBOARD AND REVIEW */}
          <div className="mcqResultActions">
            <Button
              className="mcqResultButton"
              type="primary"
              size="large"
              onClick={handleReviewAnswers}
              icon={<RateReviewIcon />}
              style={{ marginRight: "12px" }}
            >
              Review Answers
            </Button>
            <Button
              className="mcqResultButton"
              type="primary"
              size="large"
              onClick={handleDashboard}
              icon={<HomeIcon />}
            >
              Continue to Dashboard
            </Button>
          </div>
        </Card>
      </div>

      <FeedbackModal
        visible={showFeedbackModal}
        courseId={id}
        userId={userId}
        onSuccessRedirect={handleFeedbackSuccess}
      />
    </div>
  );
};

export default ManualResult;
